'use client';

import { useState } from 'react';
import posthog from 'posthog-js';
import type {
  QuizLanguage,
  QuizSource,
  SwapQuizQuestionBody,
} from '@peach/types/quizzes/generated-quiz.dto.js';
import { swapQuizQuestion } from '@/app/actions/quiz-gen';
import { Field, type EditableQuiz } from './QuizFields';

interface QuestionSwapsProps<T extends EditableQuiz> {
  quiz: T;
  /** What the quiz was written from, carried by the quiz itself so a swap is written on the
      same input. No `brief` (a quiz stored before it was kept) means edit, never swap. */
  source: QuizSource & { language?: QuizLanguage };
  onChange: (next: T) => void;
}

/**
 * The questions as a list, each with one button that writes it again, and a
 * second that opens it for editing in place — the same three fields
 * the owner gets on the storefront. A swap that fails leaves the question as
 * it was; the row says so and the button stays.
 */
export function QuestionSwaps<T extends EditableQuiz>({
  quiz,
  source,
  onChange,
}: QuestionSwapsProps<T>) {
  const { brief, audience, taught, voice, language } = source;
  const [busy, setBusy] = useState<number | null>(null);
  const [failed, setFailed] = useState<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);

  const replace = (index: number, question: T['questions'][number]) =>
    onChange({ ...quiz, questions: quiz.questions.map((q, i) => (i === index ? question : q)) });

  const swap = async (index: number) => {
    if (!brief) return;
    setBusy(index);
    setFailed(null);
    setEditing(null);
    const body: SwapQuizQuestionBody = {
      what: brief,
      audience,
      language,
      taught,
      voice,
      title: quiz.title,
      questions: quiz.questions.map((q) => q.text),
      index,
    };
    const result = await swapQuizQuestion(body);
    setBusy(null);
    posthog.capture('ai_draft_question_swapped', { index, ok: result.ok });
    if (!result.ok) {
      setFailed(index);
      return;
    }
    // The written question lands on the old one's shape, so an id it carries survives.
    const old = quiz.questions[index]!;
    replace(index, {
      ...old,
      text: result.question.text,
      options: result.question.options.map((option, k) => ({ ...old.options[k]!, ...option })),
    });
  };

  return (
    <ol className="flex flex-col gap-1.5">
      {quiz.questions.map((q, i) => {
        const answer = (correct: boolean) =>
          q.options.find((option) => option.isCorrect === correct)?.label ?? '';
        const setAnswer = (correct: boolean, label: string) =>
          replace(i, {
            ...q,
            options: q.options.map((option) =>
              option.isCorrect === correct ? { ...option, label } : option
            ),
          });

        if (editing === i) {
          return (
            <li key={i} className="flex flex-col gap-1.5 rounded-xl bg-[rgba(0,0,0,0.25)] p-3">
              <Field
                label={`Question ${i + 1}`}
                value={q.text}
                onChange={(text) => replace(i, { ...q, text })}
              />
              <Field
                label="Right answer"
                value={answer(true)}
                onChange={(label) => setAnswer(true, label)}
              />
              <Field
                label="Wrong answer"
                value={answer(false)}
                onChange={(label) => setAnswer(false, label)}
              />
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="mm-btn min-h-9 self-end rounded-lg bg-white/20 px-3 text-badge font-bold text-white"
              >
                Done
              </button>
            </li>
          );
        }

        return (
          <li key={i} className="flex items-center gap-2 rounded-xl bg-white/15 py-2.5 pl-3 pr-1.5">
            <span className="sf-num w-5 flex-none self-start pt-0.5 text-badge font-bold text-white/70">
              {i + 1}
            </span>
            <span
              aria-live="polite"
              className="flex min-w-0 flex-1 flex-col gap-0.5 text-p-3 leading-snug text-white"
            >
              <span className="font-semibold">
                {failed === i ? 'Could not write that one. Try again.' : q.text}
              </span>
              {failed !== i && (
                <span className="flex flex-wrap gap-x-3 text-badge text-white/75">
                  <span>{answer(true)}</span>
                  <span className="line-through">{answer(false)}</span>
                </span>
              )}
            </span>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => setEditing(i)}
              aria-label={`Edit question ${i + 1}`}
              className="mm-btn min-h-9 flex-none rounded-lg bg-white/20 px-3 text-badge font-bold text-white disabled:opacity-50"
            >
              Edit
            </button>
            {brief && (
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void swap(i)}
                aria-label={`Swap question ${i + 1}`}
                className="mm-btn min-h-9 flex-none rounded-lg bg-white/20 px-3 text-badge font-bold text-white disabled:opacity-50"
              >
                {busy === i ? 'Writing…' : 'Swap'}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
